import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@utils/api";

import {
  AccountType,
  EngagementDashboard,
  OnboardingInvitationCredentials,
  Prospect,
  ProspectDraft,
  ProspectInvitationAccess,
  ProspectList,
  ProspectStatus,
  ProspectSummary,
} from "@customTypes/accountManagement";


export interface ProspectFilters {
  page: number;
  pageSize: number;
  search?: string;
  status?: ProspectStatus | "";
  accountType?: AccountType | "";
  leadSource?: string;
  referrerType?: "doctor" | "recruiter" | "";
  archived?: boolean;
}

const keys = {
  all: ["account-management"] as const,
  prospects: (filters: ProspectFilters) =>
    [...keys.all, "prospects", filters] as const,
  summary: () => [...keys.all, "prospect-summary"] as const,
  prospect: (prospectId: string) =>
    [...keys.all, "prospects", prospectId] as const,
  engagement: (filters: EngagementFilters) =>
    [...keys.all, "engagement", filters] as const,
  invitationAccess: (prospectId: string) =>
    [...keys.all, "prospects", prospectId, "invitation-access"] as const,
};

export const useProspects = (filters: ProspectFilters) =>
  useQuery({
    queryKey: keys.prospects(filters),
    queryFn: async () => {
      const { data } = await api.get<ProspectList>(
        "account-management/prospects",
        {
          params: {
            page: filters.page,
            page_size: filters.pageSize,
            search: filters.search || undefined,
            status: filters.status || undefined,
            account_type: filters.accountType || undefined,
            lead_source: filters.leadSource || undefined,
            referrer_type: filters.referrerType || undefined,
            archived: filters.archived || false,
          },
        }
      );
      return data;
    },
    keepPreviousData: true,
  });

export const useProspectSummary = () =>
  useQuery({
    queryKey: keys.summary(),
    queryFn: async () => {
      const { data } = await api.get<ProspectSummary>(
        "account-management/prospects/summary"
      );
      return data;
    },
  });

export const useProspect = (prospectId: string, enabled = true) =>
  useQuery({
    queryKey: keys.prospect(prospectId),
    queryFn: async () => {
      const { data } = await api.get<Prospect>(
        `account-management/prospects/${prospectId}`
      );
      return data;
    },
    enabled: enabled && Boolean(prospectId),
  });

export const useCreateProspect = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProspectDraft) =>
      api.post<Prospect>("account-management/prospects", payload),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  });
};

export const useUpdateProspect = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      prospectId,
      payload,
    }: {
      prospectId: string;
      payload: Partial<ProspectDraft>;
    }) =>
      api.patch<Prospect>(
        `account-management/prospects/${prospectId}`,
        payload
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  });
};

export const useProspectAction = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      prospectId,
      action,
      note,
    }: {
      prospectId: string;
      action: "contact" | "invite" | "renew-invite" | "archive" | "restore";
      note?: string;
    }) => {
      const suffix = {
        contact: "status",
        invite: "invite",
        "renew-invite": "invite/renew",
        archive: "archive",
        restore: "restore",
      }[action];
      const { data } = await api.post<
        Prospect | OnboardingInvitationCredentials
      >(`account-management/prospects/${prospectId}/${suffix}`, {
        ...(action === "contact" && { status: "contacted", note }),
      });
      return data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  });
};

export const useProspectInvitationAccess = (
  prospectId: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: keys.invitationAccess(prospectId),
    queryFn: async () => {
      const { data } = await api.get<ProspectInvitationAccess>(
        `account-management/prospects/${prospectId}/invitation-access`
      );
      return data;
    },
    enabled: enabled && Boolean(prospectId),
  });

export interface EngagementFilters {
  dateRange: "7d" | "30d" | "90d" | "all" | "custom";
  accountType: AccountType | "";
  startDate?: string;
  endDate?: string;
}

export const useEngagementDashboard = (filters: EngagementFilters) =>
  useQuery({
    queryKey: keys.engagement(filters),
    queryFn: async () => {
      const { data } = await api.get<EngagementDashboard>(
        "account-management/engagement",
        {
          params: {
            date_range: filters.dateRange,
            account_type: filters.accountType || undefined,
            start_date: filters.startDate || undefined,
            end_date: filters.endDate || undefined,
          },
        }
      );
      return data;
    },
  });

// Access management shares the existing Account Management query/mutation boundary.
export const useAccessQuery = <T,>(path: string, enabled = true) => useQuery({
  queryKey: ["access", path],
  queryFn: async (): Promise<T> => (await api.get(`access/${path}`)).data,
  enabled,
  refetchOnWindowFocus: false,
});

export const useSaveAccess = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ path, ...payload }: {
      path: string; grants: Record<string, string>; revision: number; reason: string;
    }) => api.put(`access/${path}`, payload),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["access"] });
      window.dispatchEvent(new Event("permissions-changed"));
    },
  });
};
